<script setup lang="ts">
import { ref, watch } from 'vue';
import { Sidebar, useSidebar } from './sidebar';

// Emit sidebar state changes to parent
const emit = defineEmits<{
  sidebarStateChange: [state: 'expanded' | 'collapsed'];
}>();

// Access the sidebar state and methods
const { state: sidebarState, toggleSidebar } = useSidebar();
const hoverTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
const expandedByHover = ref(false);

// Watch for sidebar state changes and emit them to parent
watch(
  sidebarState,
  (newState) => {
    emit('sidebarStateChange', newState);
  },
  { immediate: true },
);

function handleSidebarMouseEnter() {
  if (hoverTimeout.value) {
    clearTimeout(hoverTimeout.value);
    hoverTimeout.value = null;
  }
  // Small delay before expanding to avoid accidental hovers
  hoverTimeout.value = setTimeout(() => {
    // Only expand if currently collapsed
    if (sidebarState.value === 'collapsed') {
      expandedByHover.value = true;
      toggleSidebar();
    }
  }, 150);
}

function handleSidebarMouseLeave() {
  if (hoverTimeout.value) {
    clearTimeout(hoverTimeout.value);
    hoverTimeout.value = null;
  }
  // Small delay before collapsing to prevent flickering
  hoverTimeout.value = setTimeout(() => {
    // Only collapse if it was expanded by hover and is currently expanded
    if (sidebarState.value === 'expanded' && expandedByHover.value) {
      expandedByHover.value = false;
      toggleSidebar();
    }
  }, 200);
}
</script>

<template>
  <Sidebar collapsible="icon" @mouseenter="handleSidebarMouseEnter" @mouseleave="handleSidebarMouseLeave">
    <slot />
  </Sidebar>
</template>
